import { StorageStrategy } from './storage-strategy.interface';

/**
 * Service that provides a flexible wrapper around storage APIs.
 * Uses a strategy for browser storage operations.
 *
 * This service is NOT a singleton - it must be created via StorageServiceFactory.
 * Each instance is bound to a specific storage strategy at construction time,
 * allowing isolated storage instances to coexist in the same component.
 *
 * Existing namespace expiry keys are honored when reading or writing values.
 *
 * Gracefully handles storage unavailability by logging errors and failing silently.
 *
 * @example
 * // Use StorageServiceFactory to create instances
 * constructor(private factory: StorageServiceFactory) {
 *   this.localStorage = factory.createLocalStorage();
 * }
 */
export class StorageService {
  private readonly EXPIRES_AT_SUFFIX = '.expires-at';

  constructor(private strategy: StorageStrategy) {}

  /**
   * Stores a value in the current storage.
   * Automatically checks if the namespace has expired before storing.
   *
   * @param key The storage key (supports dot notation)
   * @param value The value to store
   *
   * @example
   * storage.set('voice-ai.dashboard.filters.work-list', 'pending');
   * storage.set('username', 'john_doe');
   */
  set(key: string, value: string): void {
    try {
      // Check and clear expired namespace before setting new value
      if (this.isNamespaceExpired(key)) {
        const namespace = this.getNamespace(key);
        this.clearNamespace(namespace);
      }

      this.strategy.setItem(key, value);
    } catch (e) {
      console.error(`Failed to set item '${key}' in storage:`, e);
    }
  }

  /**
   * Retrieves a value from the current storage.
   * Automatically checks if the namespace has expired.
   *
   * @param key The storage key
   * @returns The stored value or null if not found or namespace expired
   */
  get(key: string): string | null {
    try {
      // Check if any parent namespace has expired
      if (this.isNamespaceExpired(key)) {
        const namespace = this.getNamespace(key);
        this.clearNamespace(namespace);
        return null;
      }

      return this.strategy.getItem(key);
    } catch (e) {
      console.error(`Failed to get item '${key}' from storage:`, e);
      return null;
    }
  }

  /**
   * Removes a value from the current storage.
   * @param key The storage key to remove
   */
  remove(key: string): void {
    try {
      this.strategy.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove item '${key}' from storage:`, e);
    }
  }

  /** Clears an expired namespace and its expiry marker. */
  private clearNamespace(namespace: string | null): void {
    if (!namespace) return;

    try {
      const prefix = `${namespace}.`;
      const keys = this.getAllKeys();

      keys.forEach(key => {
        if (key.startsWith(prefix) || key === `${namespace}${this.EXPIRES_AT_SUFFIX}`) {
          this.remove(key);
        }
      });
    } catch (e) {
      console.error(`Failed to clear namespace '${namespace}':`, e);
    }
  }

  /**
   * Checks if a namespace has an expiry date set and if it's expired.
   * @param key The key to check
   * @returns true if namespace is expired
   */
  private isNamespaceExpired(key: string): boolean {
    try {
      const namespace = this.getNamespace(key);
      if (!namespace) {
        return false;
      }

      const expiresAtKey = `${namespace}${this.EXPIRES_AT_SUFFIX}`;
      const expiresAtStr = this.strategy.getItem(expiresAtKey);

      if (!expiresAtStr) {
        return false;
      }

      const expiresAtDate = parseInt(expiresAtStr, 10);
      return Date.now() > expiresAtDate;
    } catch (e) {
      console.error('Failed to check namespace expiry:', e);
      return false;
    }
  }

  /**
   * Extracts the namespace from a key.
   * For 'voice-ai.dashboard.filters.work-list', returns 'voice-ai.dashboard.filters'
   *
   * @param key The full key
   * @returns The namespace or null if key has no namespace
   */
  private getNamespace(key: string): string | null {
    const parts = key.split('.');
    if (parts.length <= 1) {
      return null;
    }

    // Remove last part (the actual key name)
    parts.pop();
    return parts.join('.');
  }

  /**
   * Gets all keys from current storage.
   *
   * @returns Array of all keys in storage
   */
  private getAllKeys(): string[] {
    try {
      return this.strategy.getAllKeys();
    } catch (e) {
      console.error('Failed to get all keys from storage:', e);
      return [];
    }
  }
}
